import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Timer, ChevronLeft, Globe, Download, Clock, TrendingUp } from 'lucide-react';
import { useReportFilters } from './useReportFilters';
import { toast } from 'sonner';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

export default function PayrollTimesheetPage() {
  const navigate = useNavigate();
  const { timezone } = useReportFilters();

  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const monthStr = `${year}-${String(month + 1).padStart(2, '0')}`;
  const startDate = `${monthStr}-01`;
  const endDate = (() => { const d = new Date(year, month + 1, 0); return `${monthStr}-${String(d.getDate()).padStart(2,'0')}`; })();

  useEffect(() => { load(); }, [monthStr]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/payroll-reports/timesheet`, {
        params: { start_date: startDate, end_date: endDate }, withCredentials: true
      });
      setData(res.data);
    } catch { toast.error('Failed to load timesheet'); }
    finally { setLoading(false); }
  };

  const rows = data?.staff_timesheets || data?.records || [];
  const totalHours = rows.reduce((a, r) => a + (r.total_hours || 0), 0);
  const totalOT = rows.reduce((a, r) => a + (r.overtime_hours || 0), 0);

  const chartData = rows.slice(0, 12).map(r => ({
    name: r.staff_name?.split(' ')[0],
    regular: +(r.regular_hours || 0).toFixed(1),
    overtime: +(r.overtime_hours || 0).toFixed(1),
  }));

  return (
    <div className="space-y-6" data-testid="payroll-timesheet-page">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/admin/reports')} className="p-2 rounded-lg hover:bg-gray-100">
          <ChevronLeft className="w-5 h-5 text-gray-600" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">Timesheet Report</h1>
          <div className="flex items-center gap-1.5 text-sm text-gray-500 mt-0.5">
            <Globe className="w-3.5 h-3.5" /><span>{timezone}</span>
          </div>
        </div>
      </div>

      {/* Month selector */}
      <Card>
        <CardContent className="pt-5">
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <Label className="text-xs text-gray-500 mb-1 block">Year</Label>
              <Select value={String(year)} onValueChange={v => setYear(Number(v))}>
                <SelectTrigger className="h-8 text-sm w-28"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[now.getFullYear()-1, now.getFullYear()].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-gray-500 mb-1 block">Month</Label>
              <div className="flex flex-wrap gap-1">
                {MONTHS.map((m, i) => (
                  <button key={i} onClick={() => setMonth(i)}
                    className={`px-3 py-1 text-xs rounded-full border transition-all ${i === month ? 'bg-violet-600 text-white border-violet-600' : 'text-gray-600 hover:bg-gray-100'}`}>{m}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {loading && <div className="text-center py-12 text-gray-500">Loading timesheet...</div>}

      {!loading && (!data || rows.length === 0) && (
        <Card><CardContent className="py-16 text-center">
          <Timer className="w-12 h-12 mx-auto text-gray-200 mb-3" />
          <p className="text-gray-500 font-medium">No timesheet data for {MONTHS[month]} {year}</p>
          <p className="text-xs text-gray-400 mt-1">Data appears when staff have recorded attendance this month</p>
        </CardContent></Card>
      )}

      {!loading && data && rows.length > 0 && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: 'Staff', value: rows.length, c: 'text-gray-800', bg: 'bg-gray-50', Icon: Timer },
              { label: 'Total Hours', value: `${totalHours.toFixed(1)}h`, c: 'text-blue-600', bg: 'bg-blue-50', Icon: Clock },
              { label: 'Avg Hours/Staff', value: rows.length ? `${(totalHours/rows.length).toFixed(1)}h` : '0h', c: 'text-indigo-600', bg: 'bg-indigo-50', Icon: TrendingUp },
              { label: 'Overtime Hours', value: `${totalOT.toFixed(1)}h`, c: 'text-violet-600', bg: 'bg-violet-50', Icon: Timer },
            ].map(({ label, value, c, bg, Icon }) => (
              <Card key={label} className="border-0 shadow-sm">
                <CardContent className={`pt-4 ${bg} rounded-xl flex gap-3 items-center`}>
                  <Icon className={`w-7 h-7 ${c}`} />
                  <div><p className={`text-xl font-bold ${c}`}>{value}</p><p className="text-xs text-gray-500">{label}</p></div>
                </CardContent>
              </Card>
            ))}
          </div>

          {chartData.length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-base">Hours by Staff — {MONTHS[month]} {year}</CardTitle></CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={chartData} barCategoryGap="25%">
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} unit="h" />
                    <Tooltip formatter={v => `${v}h`} />
                    <Legend />
                    <Bar dataKey="regular" fill="#6366f1" name="Regular Hours" radius={[3,3,0,0]} />
                    <Bar dataKey="overtime" fill="#f59e0b" name="Overtime Hours" radius={[3,3,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="text-base">Timesheet Details</CardTitle>
                <Button size="sm" variant="outline" onClick={() => {
                  const csv = ['Staff,Dept,Working Days,Regular Hours,Overtime Hours,Total Hours,Avg Daily Hours']
                    .concat(rows.map(r => `${r.staff_name},${r.department||'–'},${r.working_days},${(r.regular_hours||0).toFixed(1)},${(r.overtime_hours||0).toFixed(1)},${(r.total_hours||0).toFixed(1)},${r.working_days?(r.total_hours/r.working_days).toFixed(1):'0'}h`))
                    .join('\n');
                  const a = document.createElement('a');
                  a.href = URL.createObjectURL(new Blob([csv],{type:'text/csv'}));
                  a.download = `timesheet-${monthStr}.csv`;
                  a.click();
                }}>
                  <Download className="w-4 h-4 mr-1" /> CSV
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>{['Staff','Dept','Working Days','Regular Hrs','Overtime Hrs','Total Hrs','Avg Daily Hrs'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-600">{h}</th>
                  ))}</tr>
                </thead>
                <tbody className="divide-y">
                  {rows.map((r, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium">{r.staff_name}</td>
                      <td className="px-4 py-3"><Badge variant="outline" className="text-xs">{r.department||'–'}</Badge></td>
                      <td className="px-4 py-3">{r.working_days || 0}</td>
                      <td className="px-4 py-3 text-blue-600">{(r.regular_hours||0).toFixed(1)}h</td>
                      <td className="px-4 py-3 text-amber-600 font-semibold">{(r.overtime_hours||0).toFixed(1)}h</td>
                      <td className="px-4 py-3 text-violet-600 font-bold">{(r.total_hours||0).toFixed(1)}h</td>
                      <td className="px-4 py-3 text-gray-600">{r.working_days > 0 ? `${((r.total_hours||0)/r.working_days).toFixed(1)}h` : '–'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
